import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, Camera, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { FieldCard } from "@/components/felt/FieldInput";
import { createSiteVisit, registerSiteVisitAttachment, updateSiteVisit } from "@/lib/site-visit.functions";
import { PROJECT_IMAGE_BUCKET, uploadProjectImage } from "@/lib/project-images";

export const Route = createFileRoute("/felt/foto")({
  head: () => ({
    meta: [
      { title: "Bilder til befaring | VERQO feltmodus" },
      {
        name: "description",
        content:
          "Ta bilder på byggeplass, knytt dem til et prosjekt og legg ved et notat – rett fra feltmodus.",
      },
      { property: "og:title", content: "Bilder til befaring – VERQO feltmodus" },
      {
        property: "og:description",
        content: "Ta bilder på byggeplass og knytt dem til prosjektet med notat.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: FeltFoto,
});

interface Pending {
  file: File;
  url: string;
}

function FeltFoto() {
  const { companyId, activeModuleKey } = useActiveCompany();
  const createFn = useServerFn(createSiteVisit);
  const attachFn = useServerFn(registerSiteVisitAttachment);
  const updateFn = useServerFn(updateSiteVisit);

  const cameraInput = useRef<HTMLInputElement>(null);
  const [projectId, setProjectId] = useState("");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState<Pending[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedVisitId, setSavedVisitId] = useState<string | null>(null);

  const projects = useQuery({
    queryKey: ["felt-projects", companyId, activeModuleKey],
    enabled: !!companyId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("id, name, project_number")
        .eq("module_key", activeModuleKey)
        .order("updated_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
  });

  function addFiles(files: FileList | null) {
    if (!files) return;
    setSavedVisitId(null);
    setPhotos((prev) => [
      ...prev,
      ...Array.from(files).map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  }

  function removePhoto(i: number) {
    setPhotos((prev) => {
      const p = prev[i];
      if (p) URL.revokeObjectURL(p.url);
      return prev.filter((_, idx) => idx !== i);
    });
  }

  async function save() {
    if (!projectId || photos.length === 0) return;
    setSaving(true);
    try {
      if (!companyId) throw new Error("Ingen aktiv bedrift");

      const visit = await createFn({
        data: { projectId, note: note.trim() || null },
      });

      for (const p of photos) {
        await uploadProjectImage({
          storage: supabase.storage.from(PROJECT_IMAGE_BUCKET) as never,
          companyId,
          projectId,
          file: p.file,
          register: async (path) => {
            await attachFn({
              data: {
                visitId: visit.id,
                storagePath: path,
                mimeType: p.file.type,
                sizeBytes: p.file.size,
                caption: null,
                kind: "image",
              },
            });
          },
        });
      }

      if (note.trim()) {
        await updateFn({ data: { visitId: visit.id, note: note.trim() } });
      }

      photos.forEach((p) => URL.revokeObjectURL(p.url));
      setPhotos([]);
      setNote("");
      setSavedVisitId(visit.id);
      toast.success("Bilder lagret på befaringen");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Lagring feilet");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <Link to="/felt" className="inline-flex min-h-12 items-center gap-2 text-sm font-medium">
        <ArrowLeft className="h-5 w-5" /> Alle beregninger
      </Link>

      <h1 className="text-2xl font-bold">Bilder til befaring</h1>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <button
        type="button"
        className="btn btn-primary h-16 w-full text-lg"
        onClick={() => cameraInput.current?.click()}
      >
        <Camera className="mr-2 h-6 w-6" /> Ta bilde
      </button>

      {photos.length > 0 && (
        <FieldCard title={`${photos.length} bilde${photos.length > 1 ? "r" : ""} klart`}>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((p, i) => (
              <div key={p.url} className="relative">
                <img
                  src={p.url}
                  alt={`Befaringsbilde ${i + 1}`}
                  className="aspect-square w-full rounded-lg object-cover"
                />
                <button
                  type="button"
                  aria-label="Fjern bilde"
                  className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-background/90 shadow"
                  onClick={() => removePhoto(i)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </FieldCard>
      )}

      {companyId ? (
        <FieldCard title="Prosjekt og notat">
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-muted-foreground">Prosjekt</span>
              <select
                className="input-base h-12 w-full text-base"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              >
                <option value="">Velg prosjekt …</option>
                {(projects.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.project_number ? `${p.project_number} – ` : ""}
                    {p.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-1 block text-sm font-medium text-muted-foreground">Notat</span>
              <textarea
                className="input-base min-h-24 w-full text-base"
                value={note}
                placeholder="Hva viser bildene?"
                onChange={(e) => setNote(e.target.value)}
              />
            </label>

            <button
              type="button"
              className="btn btn-primary h-14 w-full text-lg"
              disabled={!projectId || photos.length === 0 || saving}
              onClick={save}
            >
              {saving ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Check className="mr-2 h-5 w-5" />
              )}
              {saving ? "Lagrer …" : "Lagre på befaring"}
            </button>

            {savedVisitId && (
              <Link
                to="/projects/$id/visit"
                params={{ id: projectId }}
                className="btn btn-secondary h-12 w-full"
              >
                Åpne befaringen
              </Link>
            )}
          </div>
        </FieldCard>
      ) : (
        <p className="text-sm text-muted-foreground">
          Logg inn for å knytte bilder til et prosjekt.
        </p>
      )}
    </div>
  );
}
